<template>
  <Modal ref="modalRef" @after-close="clearForm">
    <template #header>Create new agent</template>

    <template #body>
      <FormGroup class="form">
        <FormField label="First Name" :error="form.fieldErrors('firstName')">
          <Input v-model="form.fields.firstName" />
        </FormField>

        <FormField label="Last Name" :error="form.fieldErrors('lastName')">
          <Input v-model="form.fields.lastName" />
        </FormField>

        <FormField label="Phone number" :error="form.fieldErrors('phone')">
          <InputPhone v-model="form.fields.phone" />
        </FormField>
      </FormGroup>
    </template>

    <template #footer>
      <Button variant="ghost" @click="modalRef?.close">Cancel</Button>
      <Button @click="save" :loading="form.submitting">Create</Button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { useQueryCache } from '@pinia/colada';
import { helpers, required } from '@vuelidate/validators';
import { ref } from 'vue';

import {
  phoneNumberValidator,
  Button,
  FormField,
  FormGroup,
  Input,
  InputPhone,
  Modal,
  useForm,
} from '@tallo/design-system';

import { SaveShowingAgentDto } from '../interfaces/save-showing-agent.dto';
import { ShowingAgentDto } from '../interfaces/showing-agent.dto';
import { showingAgentsQueryKey } from '../queries/showing-agents.query';
import showingAgentsService from '../services/showing-agents.service';

const emit = defineEmits<{ created: [showingAgent: ShowingAgentDto] }>();

const queryCache = useQueryCache();
const modalRef = ref<InstanceType<typeof Modal>>();
const initialFormValue = {
  firstName: '',
  lastName: '',
  phone: '',
};

const form = useForm<SaveShowingAgentDto>(initialFormValue, {
  firstName: {
    required: helpers.withMessage('First name is required', required),
  },
  lastName: {
    required: helpers.withMessage('Last name is required', required),
  },
  phone: {
    phoneNumberValidator,
    required: helpers.withMessage('Phone number is required', required),
  },
});

function save() {
  form.submit(async () => {
    const showingAgent = await showingAgentsService.create(form.fields);

    await queryCache.invalidateQueries({ key: [showingAgentsQueryKey] });

    modalRef.value?.close();
    emit('created', showingAgent);
  });
}

function clearForm() {
  Object.assign(form.fields, { ...initialFormValue });
}

defineExpose({ modalRef });
</script>

<style scoped lang="scss">
.form {
  width: 27rem;
  max-width: 100%;
}
</style>
